import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import { FaBell, FaPencilAlt } from "react-icons/fa";
import axios from "axios";
import logo from "../image/logo.png";
import Loader from "./Loader";
import { useTheme } from '../components/ThemeContext';
import { FaSun, FaMoon } from "react-icons/fa";
import { ensureValidToken, isOfflineMode } from "../utils/authService";

const Header = ({ toggleSidebar, handleClick }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileNumber, setProfileNumber] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileId, setProfileId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const { theme, toggleTheme } = useTheme();


  // Fetch user profile data and check for unread notifications
  useEffect(() => {
    const fetchData = async () => {
      // Skip API calls when offline
      if (isOfflineMode()) {
        return;
      }

      try {
        const accessToken = await ensureValidToken();
        if (!accessToken) {
          console.error("Access token not found.");
          return;
        }

        // Fetch user profile
        const profileResponse = await axios.get("https://api.nexcard.co.in/api/profile/", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (profileResponse.data.results.length > 0) {
          const userProfile = profileResponse.data.results[0];
          setProfileId(userProfile.id);
          setProfileName(userProfile.full_name || "");
          setProfileNumber(userProfile.phone_number || "");
          if (userProfile.profile_image) {
            setProfileImage(userProfile.profile_image);
          }
        }

        // Check for unread notifications
        const notificationsResponse = await axios.get(
          "https://api.nexcard.co.in/api/cards/request/invitations/",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        // Check if there are any unread notifications
        const hasUnread = notificationsResponse.data.data?.some(
          (notification) => !notification.is_accepted
        );
        setHasUnreadNotifications(hasUnread);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();

    // Set up polling for new notifications (every 60 seconds) - only when online
    const pollInterval = setInterval(() => {
      if (!isOfflineMode()) {
        fetchData();
      }
    }, 60000);
    return () => clearInterval(pollInterval);
  }, []);

  const handleProfileClick = () => {
    setIsModalOpen(true);
  };

  const handleNotificationClick = () => {
    setHasUnreadNotifications(false); // Mark as read when clicked
    navigate("/notification");
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (isOfflineMode()) {
      alert("You are offline. Updating profile picture is disabled in offline mode.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken || !profileId) {
        throw new Error("Authentication required");
      }

      const formData = new FormData();
      formData.append("profile_image", file);

      const response = await axios.patch(
        `https://api.nexcard.co.in/api/profile/${profileId}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.profile_image) {
        setProfileImage(response.data.profile_image);
      }
    } catch (error) {
      console.error("Error updating profile image:", error);
      setError("Failed to update profile picture. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="header">
      <div className="header-container">
        <div className="logo-section">
          <img src={logo} alt="NexCard Logo" className="logo" />
          <div className="logo-text">
            <h1>NexCard</h1>
            <p>The Next Generation of Business Cards</p>
          </div>
        </div>
        <div className="profile-section">
          <div className="small-toggle">
            <input
              type="checkbox"
              id="theme-switch"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <label htmlFor="theme-switch" className="switch">
              <span className="slider">
                {theme === 'dark' ? <FaMoon size={14} /> : <FaSun size={14} />}
              </span>
            </label>
          </div>
          <div className="notification">
            <button className="a" onClick={handleNotificationClick}>
              <FaBell className="bell-icon" />
              {hasUnreadNotifications && <span className="notification-dot"></span>}
            </button>
          </div>
          <div className="profile-info" onClick={handleProfileClick}>
            <div className="profile-text">
              <h4 className="profile-name">{profileName}</h4>
              <h4 className="profile-number">{profileNumber}</h4>
            </div>
            <img
              src={
                profileImage ||
                "https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png"
              }
              alt="Profile"
              className="profile-img"
            />
          </div>
        </div>
        <div className="menu-icon" onClick={toggleSidebar}>
          <FiMenu size={30} />
        </div>
      </div>

      {/* Profile Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Profile</h2>
              <button onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="profile-image-edit">
                <img
                  src={
                    profileImage ||
                    "https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png"
                  }
                  alt="Profile"
                  className="profile-img"
                />
                <label htmlFor="profile-image-upload" className="edit-icon">
                  {isLoading ? <Loader small /> : <FaPencilAlt />}
                </label>
                <input
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                  disabled={isLoading}
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <div className="form-group">
              <h4 className="profile-name" style={{textAlign:"center"}}>{profileName}</h4>
              </div>
              <div className="form-group">
              <h4 className="profile-number" style={{textAlign:"center"}}>{profileNumber}</h4>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;